import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import { z } from 'zod'

import {
  choiceOptionFilterShape,
  surveyInstanceCreateShape,
  surveyInstanceShape,
  surveyResponseCreateShape,
  surveyResponseShape,
  surveyQuestionOptionShape,
  surveyInstanceReportShape,
  surveyResponseUpdateShape,
  presignedUploadInputShape,
  presignedUploadOutputShape,
  presignedDownloadInputShape,
  presignedDownloadOutputShape,
  confirmUploadInputShape,
  confirmUploadOutputShape,
} from './models'

export const getSurveyInstance = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string(),
    outputShape: surveyInstanceShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(`/survey/instance/${input}/`)
    return utils.fromApi(res.data)
  },
})

export const getSurveyInstanceReport = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string(),
    outputShape: surveyInstanceReportShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(`/survey/instance/${input}/report/`)
    return utils.fromApi(res.data)
  },
})

export const createSurveyInstance = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: surveyInstanceCreateShape,
    outputShape: surveyInstanceShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post('/survey/instance/', utils.toApi(input))
    return utils.fromApi(res.data)
  },
})

export const getSurveyCursor = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string(),
    outputShape: surveyInstanceShape,
  },
  cb: async ({ client, utils, input }) => {
    const res = await client.get(`/survey/instance/cursor/${input}/`)
    return utils.fromApi(res.data)
  },
})

export const getPreviousQuestion = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string(),
    outputShape: surveyInstanceShape,
  },
  cb: async ({ client, utils, input }) => {
    const res = await client.get(`/survey/instance/${input}/previous-question/`)
    return utils.fromApi(res.data)
  },
})

// Mark a survey instance as completed (sets completion_date_time). Sent when the
// guest/admin answers the final question so TRA/SIRE submission is unblocked — the
// backend only honors `mark_complete` on the instance serializer, not the response.
export const completeSurveyInstance = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string().uuid(),
    outputShape: surveyInstanceShape,
  },
  cb: async ({ client, utils, input }) => {
    const res = await client.patch(`/survey/instance/${input}/`, { mark_complete: true })
    return utils.fromApi(res.data)
  },
})

export const createSurveyResponse = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: surveyResponseCreateShape,
    outputShape: surveyResponseShape,
  },
  cb: async ({ client, utils, input }) => {
    const res = await client.post('/survey/response/', utils.toApi(input))
    return utils.fromApi(res.data)
  },
})

export const updateSurveyResponse = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: surveyResponseUpdateShape,
    outputShape: surveyResponseShape,
  },
  cb: async ({ client, utils, input }) => {
    const { id, ...rest } = utils.toApi(input)
    const res = await client.patch(`/survey/response/${id}/`, rest)
    return utils.fromApi(res.data)
  },
})

export const surveyQuestionCOapi = createApi({
  client: axiosInstance,
  baseUri: '/survey/choice-options/',
  models: {
    entity: surveyQuestionOptionShape,
    extraFilters: choiceOptionFilterShape,
  },
})

// ============================================================================
// File Upload Presigned URL API
// ============================================================================

/**
 * Get a presigned URL for uploading a file to S3.
 * Returns the upload URL and object key to store as the response value.
 */
export const getPresignedUploadUrl = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: presignedUploadInputShape,
    outputShape: presignedUploadOutputShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post('/survey/presigned-upload/', utils.toApi(input))
    return utils.fromApi(res.data)
  },
})

/**
 * Get a presigned URL for downloading a file from S3.
 */
export const getPresignedDownloadUrl = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: presignedDownloadInputShape,
    outputShape: presignedDownloadOutputShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post('/survey/presigned-download/', utils.toApi(input))
    return utils.fromApi(res.data)
  },
})

/**
 * Confirm an uploaded file with backend validation.
 */
export const confirmUpload = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: confirmUploadInputShape,
    outputShape: confirmUploadOutputShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post('/survey/confirm-upload/', utils.toApi(input))
    return utils.fromApi(res.data)
  },
})

/**
 * Upload a file to S3 using a presigned PUT URL.
 * This is a direct S3 upload, not through our API.
 */
export const uploadFileToS3 = async (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'))
    })

    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}
